import { useState } from 'react';
import { toast } from 'sonner';
import { CalendarDays, Plus, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { useCurrentUser } from '../../hooks/useAuth';
import { useMobilisationsList } from '../../hooks/useMobilisations';
import { usePeopleMemberships } from '../../hooks/usePeopleMemberships';
import {
  useAttachPersonMobilisation,
  useDetachPersonMobilisation,
} from '../../hooks/useMobilisationAttendees';
import { ApiError } from '../../lib/api';
import { formatDate } from '../../lib/dateFmt';

const MANAGE_ROLES = ['admin', 'super_admin', 'coordinator'];

/**
 * Which mobilisation days this person belongs to, with add / remove
 * for admin and coordinator (HR sees it read-only; the API gates the
 * writes the same way). Removal is refused by the API once the person
 * has events in that mobilisation, so the audit chain stays whole.
 */
export function PersonMobilisationsPanel({
  personId,
  personName,
}: {
  personId: string;
  personName: string;
}) {
  const { data: user } = useCurrentUser();
  const memberships = usePeopleMemberships();
  const list = useMobilisationsList();
  const attach = useAttachPersonMobilisation();
  const detach = useDetachPersonMobilisation();
  const [pick, setPick] = useState('');

  const mine = memberships.data?.[personId] ?? [];
  const canManage = user?.roles.some((r) => MANAGE_ROLES.includes(r)) ?? false;
  const addable = (list.data ?? []).filter(
    (m) => m.kind !== 'onboarding' && !mine.some((x) => x.id === m.id),
  );

  async function onAdd() {
    if (!pick) return;
    try {
      await attach.mutateAsync({ mobilisation_id: pick, person_id: personId });
      setPick('');
      toast.success(`Added ${personName}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'add failed');
    }
  }

  async function onRemove(mobilisation_id: string, name: string) {
    try {
      await detach.mutateAsync({ mobilisation_id, person_id: personId });
      toast.success(`Removed from ${name}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'remove failed');
    }
  }

  return (
    <section className="overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
      <div className="flex items-center gap-2 border-b border-line/10 px-5 py-3">
        <CalendarDays className="h-4 w-4 text-fg-muted" />
        <h2 className="text-sm font-semibold">Mobilisations</h2>
      </div>
      <div className="p-5 sm:p-6">
        {memberships.isLoading ? (
          <p className="text-sm text-fg-muted">Loading...</p>
        ) : mine.length === 0 ? (
          <p className="rounded-md bg-warning-soft/15 px-3 py-2 text-sm text-warning-soft-fg">
            Not in any mobilisation day. Reception and station staff cannot
            see this person until they are added to one.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {mine.map((m) => (
              <li
                key={m.id}
                className="inline-flex items-center gap-1 rounded-full bg-accent/10 py-0.5 pl-2.5 pr-1 text-xs font-medium text-accent"
              >
                {m.name}
                {canManage ? (
                  <button
                    type="button"
                    onClick={() => onRemove(m.id, m.name)}
                    disabled={detach.isPending}
                    aria-label={`Remove from ${m.name}`}
                    title={`Remove from ${m.name}`}
                    className="rounded-full p-0.5 transition-colors hover:bg-accent/15"
                  >
                    <X className="h-3 w-3" />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {canManage && addable.length > 0 ? (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <select
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              aria-label="Add to mobilisation"
              className="h-9 min-w-[220px] rounded-md border border-line-strong/20 bg-surface px-2 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
            >
              <option value="">Add to a mobilisation...</option>
              {addable.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({formatDate(m.date)}, {m.status})
                </option>
              ))}
            </select>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Plus className="h-3.5 w-3.5" />}
              disabled={!pick}
              loading={attach.isPending}
              onClick={onAdd}
            >
              Add
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
